import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CheckCircle2, AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

type ArticleStatus = "current" | "overdue" | "never-read";
type Response = {
  mandatory: boolean;
  status: ArticleStatus | null;
  acknowledgedAt: string | null;
  articleVersion: string | null;
  ackVersion: string | null;
};

/**
 * Footer rendered under any logged-in article view (`/knowledge/<slug>`).
 * Hides itself for non-mandatory articles. For mandatory articles it shows:
 *   - "Already acknowledged on <date>" when the user is current.
 *   - A re-ack panel with a fresh "I have read this" checkbox + button when
 *     the article was updated since the user's last ack OR the ack is older
 *     than the annual freshness window (server returns status === "overdue").
 *   - The first-time ack panel when the user has never acked.
 *
 * Uses /api/security-training/acknowledge so all the same server-side
 * guard rails apply (body must be non-empty, slug must be mandatory or
 * required, ack is captured with the current article version).
 */
export function SecurityTrainingAcknowledge({ slug }: { slug: string }) {
  const { toast } = useToast();
  const [confirmed, setConfirmed] = useState(false);

  const { data, isLoading } = useQuery<Response>({
    queryKey: ["/api/security-training/article-status", slug],
  });

  const ackMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/security-training/acknowledge", { articleSlug: slug });
      return res.json();
    },
    onSuccess: () => {
      setConfirmed(false);
      queryClient.invalidateQueries({ queryKey: ["/api/security-training/article-status", slug] });
      queryClient.invalidateQueries({ queryKey: ["/api/security-training/my-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/training-status"] });
      toast({ title: "Acknowledgement recorded", description: "Thank you — your training record has been updated." });
    },
    onError: (err: any) => {
      toast({
        title: "Could not record your acknowledgement",
        description: err?.message || "Try again in a moment.",
        variant: "destructive",
      });
    },
  });

  if (isLoading || !data || !data.mandatory) return null;

  const ackDate = data.acknowledgedAt ? new Date(data.acknowledgedAt) : null;

  if (data.status === "current") {
    return (
      <div
        className="mt-10 flex items-start gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-900/60 dark:bg-emerald-950/30"
        data-testid="banner-training-current"
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        <div className="text-sm">
          <div className="font-semibold text-emerald-900 dark:text-emerald-200">
            You acknowledged this article
          </div>
          <div className="text-emerald-900/90 dark:text-emerald-200/90">
            Recorded {ackDate ? ackDate.toLocaleString() : "—"}. You'll be reminded again before
            your annual re-attestation is due, or sooner if the article is updated.
          </div>
        </div>
      </div>
    );
  }

  const headline = data.status === "overdue" ? "Re-acknowledgement needed" : "Mandatory training";
  const subline = data.status === "overdue"
    ? ackDate
      ? `You last acknowledged this on ${ackDate.toLocaleDateString()}. The article has changed or your prior acknowledgement has expired — please confirm again.`
      : "This article needs your acknowledgement."
    : "Confirm you have read and understood this required security training article.";

  return (
    <div
      className="mt-10 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30"
      data-testid="panel-training-acknowledge"
    >
      <div className="flex items-start gap-3">
        {data.status === "overdue" ? (
          <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
        ) : (
          <ShieldCheck className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
        )}
        <div className="flex-1 text-sm">
          <div className="font-semibold text-amber-900 dark:text-amber-200">{headline}</div>
          <div className="text-amber-900/90 dark:text-amber-200/90">{subline}</div>
          <label
            className="mt-3 flex items-center gap-2 cursor-pointer select-none"
            data-testid="label-training-confirm"
          >
            <Checkbox
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(v === true)}
              data-testid="checkbox-training-confirm"
            />
            <span>I have read and understood this article.</span>
          </label>
          <div className="mt-3 flex justify-end">
            <Button
              disabled={!confirmed || ackMutation.isPending}
              onClick={() => ackMutation.mutate()}
              data-testid="button-training-acknowledge"
            >
              {ackMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Recording…
                </>
              ) : data.status === "overdue" ? (
                <>Re-acknowledge<CheckCircle2 className="h-4 w-4 ml-2" /></>
              ) : (
                <>Acknowledge<CheckCircle2 className="h-4 w-4 ml-2" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
